/**
 * Card «Dati della pratica» del contesto operatore (v3 e v4, admin e area
 * associativa): i campi facoltativi del canale «Documento Unico» (targa, tipo
 * formalità, numero ticket ACI / DTT) con le etichette attuali del canale.
 *
 * - Si rende solo se il canale del ticket prevede i campi o se il ticket ha
 *   valori salvati (ticket trasferito altrove: valori con etichette
 *   predefinite, senza modifica perché il backend risponderebbe 409).
 * - Ogni valore è copiabile; la targa in monospace.
 * - Modifica come le altre Proprietà: stesso permesso (`canHandle`), stessi
 *   toast, sul 409 avviso e rilettura. Mai sui ticket chiusi.
 */
import React, { useEffect, useId, useState } from 'react';
import { Copy } from 'lucide-react';
import { useToast } from '../../../contexts/ToastContext';
import {
  CHIAVE_MONOSPACE, campiRichiestaDi, copiaNegliAppunti, righeDatiPratica, valoriDaInviare,
} from '../campiPratica';
import { isLimitedViewer } from './actionsModel';
import type { CampiExtra, OperatorSupportApi, TicketDetail } from '../../../services/supportApi';

export interface DatiPraticaCardProps {
  ticket: TicketDetail;
  api: OperatorSupportApi;
  canHandle: boolean;
  onChanged: () => void | Promise<void>;
  /** Errori diversi dal 409 (401/403/500): li gestisce il contenitore. */
  onActionError: (e: unknown) => void;
}

const DatiPraticaCard: React.FC<DatiPraticaCardProps> = ({ ticket, api, canHandle, onChanged, onActionError }) => {
  const toast = useToast();
  const headingId = useId();
  const idBase = useId();
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState<Record<string, string>>({});
  /** Valori restituiti dal salvataggio: si mostrano subito, fino al ticket riletto. */
  const [saved, setSaved] = useState<CampiExtra | null>(null);
  useEffect(() => { setSaved(null); }, [ticket]);
  // Un aggiornamento in tempo reale dello stesso ticket non butta via la modifica in corso.
  useEffect(() => { setEditing(false); }, [ticket._id]);

  const view = saved ? { ...ticket, campiExtra: saved } : ticket;
  const righe = righeDatiPratica(view);
  const campi = campiRichiestaDi(ticket.campiRichiesta);
  if (righe.length === 0) return null;

  // Stessa regola del cambio di stato nelle Proprietà: permesso di lavorazione
  // e non invitato né canale in copia (il backend risponde 403 a questi ultimi).
  const canEdit = canHandle && !isLimitedViewer(ticket.viewerRole) && Boolean(api.setCampiExtra)
    && campi.length > 0 && ticket.status !== 'chiuso';

  const startEdit = () => {
    const iniziali: Record<string, string> = {};
    righe.forEach((r) => { iniziali[r.chiave] = r.valore; });
    setDraft(iniziali);
    setEditing(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      const next = await api.setCampiExtra!(ticket._id, valoriDaInviare(campi, draft) || {});
      setSaved(next || {});
      setEditing(false);
      toast.success('Dati della pratica aggiornati');
      await onChanged();
    } catch (err: any) {
      if (err?.response?.status === 409) {
        toast.warning(err.response?.data?.error || 'Il ticket è cambiato: ricarico i dati aggiornati');
        setEditing(false);
        await onChanged();
      } else {
        onActionError(err);
      }
    } finally {
      setBusy(false);
    }
  };

  const copia = async (etichetta: string, valore: string) => {
    if (await copiaNegliAppunti(valore)) toast.success(`${etichetta} copiato`);
    else toast.error('Copia non riuscita: seleziona il testo e copialo a mano');
  };

  return (
    <section className="s3-card s3-prop s3-pratica" aria-labelledby={headingId} aria-busy={busy || undefined}>
      <div className="s3-pratica__head">
        <h3 id={headingId} className="s3-h3">Dati della pratica</h3>
        {canEdit && !editing && (
          <button
            type="button"
            className="s3-pill s3-pill--sm s3-prop__add"
            onClick={startEdit}
            aria-label="Modifica dati della pratica"
          >
            Modifica
          </button>
        )}
      </div>

      {editing && canEdit ? (
        <form
          className="s3-pratica__form"
          onSubmit={(e) => { e.preventDefault(); if (!busy) save(); }}
        >
          {campi.map((c) => {
            const id = `${idBase}-${c.chiave}`;
            return (
              <div key={c.chiave} className="s3-field">
                <label className="s3-label" htmlFor={id}>{c.etichetta}</label>
                <input
                  id={id}
                  type="text"
                  className={`s3-search s3-search--plain${c.chiave === CHIAVE_MONOSPACE ? ' s3-pratica__mono' : ''}`}
                  value={draft[c.chiave] || ''}
                  maxLength={c.maxLength}
                  autoComplete="off"
                  onChange={(e) => {
                    const v = e.target.value;
                    setDraft((prev) => ({ ...prev, [c.chiave]: v }));
                  }}
                  disabled={busy}
                />
              </div>
            );
          })}
          <p className="s3-prop__note">Tutti facoltativi: un campo vuoto viene tolto.</p>
          <div className="s3-pratica__actions">
            <button type="button" className="s3-pill s3-pill--sm" onClick={() => setEditing(false)} disabled={busy}>
              Annulla
            </button>
            <button type="submit" className="s3-pill s3-pill--sm s3-pill--primary" disabled={busy}>
              Salva
            </button>
          </div>
        </form>
      ) : (
        righe.map((r) => (
          <div key={r.chiave} className="s3-prop__row">
            <span className="s3-label s3-prop__k">{r.etichetta}</span>
            {r.valore ? (
              <span className="s3-pratica__val">
                <span className={`s3-prop__val s3-pratica__txt${r.chiave === CHIAVE_MONOSPACE ? ' s3-pratica__mono' : ''}`}>
                  {r.valore}
                </span>
                <button
                  type="button"
                  className="s3-pratica__copy"
                  onClick={() => copia(r.etichetta, r.valore)}
                  aria-label={`Copia ${r.etichetta}`}
                  title="Copia"
                >
                  <Copy size={12} aria-hidden="true" />
                </button>
              </span>
            ) : (
              <span className="s3-prop__val">—</span>
            )}
          </div>
        ))
      )}
    </section>
  );
};

export default DatiPraticaCard;
